import { FUNCTION_BASE } from "../lib/constants";
import { authFetch } from "../lib/api";
import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { FaChartBar, FaMousePointer, FaDollarSign, FaPercentage, FaFilter, FaDownload, FaPrint, FaChevronDown, FaTimes, FaCheck } from "react-icons/fa";
import "../styles/Reports.css";


const OBJECTIVES = [
  { id: "OUTCOME_AWARENESS",     label: "Awareness" },
  { id: "OUTCOME_TRAFFIC",       label: "Traffic" },
  { id: "OUTCOME_ENGAGEMENT",    label: "Engagement" },
  { id: "OUTCOME_LEADS",         label: "Leads" },
  { id: "OUTCOME_SALES",         label: "Sales" },
  { id: "OUTCOME_APP_PROMOTION", label: "App Promotion" },
];

const KpiCard = ({ title, value, icon }) => (
  <div className="kpi-card">
    <div className="kpi-card-top">
      <div className="kpi-card-icon">{icon}</div>
    </div>
    <p className="kpi-card-label">{title}</p>
    <p className="kpi-card-value">{value}</p>
  </div>
);

const ReportsPage = () => {
  const [reportsData, setReportsData]   = useState([]);
  const [kpis, setKpis]                 = useState([
    { title: "Impressions", value: "--", icon: <FaChartBar /> },
    { title: "Clicks",      value: "--", icon: <FaMousePointer /> },
    { title: "Spend",       value: "--", icon: <FaDollarSign /> },
    { title: "CTR",         value: "--", icon: <FaPercentage /> },
  ]);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [objectiveFilter, setObjectiveFilter] = useState([]);
  const [showFilterMenu, setShowFilterMenu]   = useState(false);
  const [dateFrom, setDateFrom]         = useState("");
  const [dateTo, setDateTo]             = useState("");
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [page, setPage]                 = useState(1);
  const PER_PAGE = 10;
  const filterRef                       = useRef(null);
  const [fbAccounts, setFbAccounts]     = useState({});
  const [selectedAccount, setSelectedAccount] = useState("");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const loadAccounts = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const data = snap.data();
        const accounts = data.fbAccounts || {};
        setFbAccounts(accounts);
        setSelectedAccount(data.selectedAdAccount || Object.keys(accounts)[0] || "");
      }
    };
    loadAccounts();
  }, []);

  useEffect(() => {
    const fetchInsights = async () => {
      const user = auth.currentUser;
      if (!user) { setLoading(false); return; }
      setLoading(true);
      setError(null);
      try {
        const accountParam = selectedAccount ? `&adAccountId=${encodeURIComponent(selectedAccount)}` : "";
        const params = dateFrom && dateTo
          ? `since=${dateFrom}&until=${dateTo}${accountParam}`
          : `datePreset=last_30d${accountParam}`;
        const res = await authFetch(`${FUNCTION_BASE}/api/fb/insights/${user.uid}?${params}`);
        const data = await res.json();
        if (!res.ok) { setError(data.error || "Failed to load reports"); setLoading(false); return; }

        const acct = data.accountInsights?.[0] || {};
        setKpis([
          { title: "Impressions", value: parseInt(acct.impressions || 0).toLocaleString(), icon: <FaChartBar /> },
          { title: "Clicks",      value: parseInt(acct.clicks || 0).toLocaleString(),      icon: <FaMousePointer /> },
          { title: "Spend",       value: `$${parseFloat(acct.spend || 0).toFixed(2)}`,     icon: <FaDollarSign /> },
          { title: "CTR",         value: acct.ctr ? `${parseFloat(acct.ctr).toFixed(2)}%` : "--", icon: <FaPercentage /> },
        ]);

        const rows = (data.campaigns || []).map(c => {
          const ins = c.insights?.data?.[0] || {};
          return {
            campaign:    c.name,
            status:      c.status,
            objective:   c.objective || "",
            dateStart:   ins.date_start || null,
            dateStop:    ins.date_stop  || null,
            impressions: parseInt(ins.impressions || 0),
            reach:       parseInt(ins.reach || 0),
            clicks:      parseInt(ins.clicks || 0),
            ctr:         ins.ctr   ? parseFloat(ins.ctr)   : null,
            cpc:         ins.cpc   ? parseFloat(ins.cpc)   : null,
            cpm:         ins.cpm   ? parseFloat(ins.cpm)   : null,
            spend:       parseFloat(ins.spend || 0),
          };
        });
        setReportsData(rows);
      } catch (err) {
        setError("Failed to load reports");
      } finally {
        setLoading(false);
      }
    };
    if (selectedAccount) fetchInsights();
  }, [dateFrom, dateTo, selectedAccount]);

  const objectiveLabel = (id) => OBJECTIVES.find(o => o.id === id)?.label || id;

  const toggleStatus    = (s) => setStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const toggleObjective = (o) => setObjectiveFilter(prev => prev.includes(o) ? prev.filter(x => x !== o) : [...prev, o]);

  const dateActive    = !!(dateFrom && dateTo);
  const filtersActive = statusFilter.length > 0 || objectiveFilter.length > 0;
  const filterCount   = statusFilter.length + objectiveFilter.length;

  const filteredData = reportsData
    .filter(r => statusFilter.length    === 0 || statusFilter.includes(r.status))
    .filter(r => objectiveFilter.length === 0 || objectiveFilter.includes(r.objective))
    .filter(r => r.campaign.toLowerCase().includes(search.toLowerCase()));

  const totalPages = Math.ceil(filteredData.length / PER_PAGE);
  const pagedData  = filteredData.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const clearFilters = () => { setStatusFilter([]); setObjectiveFilter([]); setDateFrom(""); setDateTo(""); setPage(1); };

  useEffect(() => { setPage(1); }, [search, statusFilter, objectiveFilter, dateFrom, dateTo]);

  const handleExport = () => {
    const headers = ["Campaign", "Status", "Objective", "Date Range", "Impressions", "Reach", "Clicks", "CTR", "Spend", "CPC", "CPM"];
    const rows = filteredData.map(r => [
      `"${r.campaign}"`,
      r.status || "-",
      objectiveLabel(r.objective),
      r.dateStart && r.dateStop ? `${r.dateStart} – ${r.dateStop}` : "-",
      r.impressions,
      r.reach,
      r.clicks,
      r.ctr  != null ? `${r.ctr.toFixed(2)}%`  : "-",
      `$${r.spend.toFixed(2)}`,
      r.cpc  != null ? `$${r.cpc.toFixed(2)}`  : "-",
      r.cpm  != null ? `$${r.cpm.toFixed(2)}`  : "-",
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `adsoar-report-${dateFrom || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => window.print();

  return (
    <div>
      {/* Header */}
      <div className="reports-header">
        <div>
          <h1 className="reports-title">Reports</h1>
          <p className="reports-subtitle">Campaign performance from your connected Meta ad account</p>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="reports-kpi-grid">
        {kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
      </div>

      {/* Toolbar */}
      <div className="reports-toolbar">
        <input
          className="reports-search-input"
          type="text"
          placeholder="Search campaigns..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {Object.keys(fbAccounts).length > 1 && (
          <select
            className="reports-account-select"
            value={selectedAccount}
            onChange={e => setSelectedAccount(e.target.value)}
          >
            {Object.entries(fbAccounts).map(([id, acc]) => (
              <option key={id} value={id}>{acc.name || id}</option>
            ))}
          </select>
        )}
        <div className="reports-toolbar-actions">

          {/* Filter dropdown */}
          <div className="reports-filter-wrap" ref={filterRef}>
            <button
              className={`reports-action-btn${filtersActive ? " reports-action-btn-active" : ""}`}
              onClick={() => setShowFilterMenu(p => !p)}
            >
              <FaFilter style={{ fontSize: "0.75rem" }} />
              Filters {filterCount > 0 && <span className="reports-filter-count">{filterCount}</span>}
              <FaChevronDown style={{ fontSize: "0.65rem" }} />
            </button>

            {showFilterMenu && (
              <div className="reports-filter-menu">
                <div className="reports-filter-section">
                  <div className="reports-filter-label">Status</div>
                  {["ACTIVE", "PAUSED"].map(s => (
                    <button
                      key={s}
                      className={`reports-filter-option${statusFilter.includes(s) ? " active" : ""}`}
                      onClick={() => toggleStatus(s)}
                    >
                      <span className="reports-filter-check">
                        {statusFilter.includes(s) && <FaCheck style={{ fontSize: "0.6rem" }} />}
                      </span>
                      {s.charAt(0) + s.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
                <div className="reports-filter-divider" />
                <div className="reports-filter-section">
                  <div className="reports-filter-label">Objective</div>
                  {OBJECTIVES.map(o => (
                    <button
                      key={o.id}
                      className={`reports-filter-option${objectiveFilter.includes(o.id) ? " active" : ""}`}
                      onClick={() => toggleObjective(o.id)}
                    >
                      <span className="reports-filter-check">
                        {objectiveFilter.includes(o.id) && <FaCheck style={{ fontSize: "0.6rem" }} />}
                      </span>
                      {o.label}
                    </button>
                  ))}
                </div>
                {filtersActive && (
                  <>
                    <div className="reports-filter-divider" />
                    <div className="reports-filter-section">
                      <button className="reports-filter-clear" onClick={() => { clearFilters(); setShowFilterMenu(false); }}>
                        <FaTimes style={{ fontSize: "0.65rem" }} /> Clear all filters
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <button className="reports-action-btn" onClick={handleExport} disabled={filteredData.length === 0}>
            <FaDownload style={{ fontSize: "0.75rem" }} /> Export CSV
          </button>

          <button className="reports-action-btn" onClick={handlePrint}>
            <FaPrint style={{ fontSize: "0.75rem" }} /> Print
          </button>

        </div>
      </div>

      {/* Date Range — below search bar */}
      <div className="reports-date-bar">
        <span className="reports-date-label">Date Range</span>
        <input type="date" className="reports-date-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        <span className="reports-date-arrow">→</span>
        <input type="date" className="reports-date-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        {dateActive
          ? <button className="reports-date-clear" onClick={() => { setDateFrom(""); setDateTo(""); }}><FaTimes style={{ fontSize: "0.65rem" }} /> Clear</button>
          : <span className="reports-date-hint">Defaults to last 30 days</span>}
      </div>

      {/* Active filter pills */}
      {filtersActive && (
        <div className="reports-active-filters">
          {statusFilter.map(s => (
            <span key={s} className="reports-filter-pill">
              {s.charAt(0) + s.slice(1).toLowerCase()}
              <button onClick={() => toggleStatus(s)}><FaTimes /></button>
            </span>
          ))}
          {objectiveFilter.map(o => (
            <span key={o} className="reports-filter-pill">
              {objectiveLabel(o)}
              <button onClick={() => toggleObjective(o)}><FaTimes /></button>
            </span>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="reports-table-card">
        {loading ? (
          <div className="reports-loading">Loading...</div>
        ) : error ? (
          <div className="reports-error">{error}</div>
        ) : (
          <>
            <div className="reports-table-scroll">
              <table className="reports-table">
                <thead>
                  <tr>
                    {["Campaign", "Status", "Objective", "Date Range", "Impressions", "Reach", "Clicks", "CTR", "Spend", "CPC", "CPM"].map(h => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr><td className="td-empty" colSpan={11}>No campaigns match your filters</td></tr>
                  ) : pagedData.map((r, idx) => (
                    <tr key={idx}>
                      <td className="td-name">{r.campaign}</td>
                      <td>
                        <span className={`report-status ${r.status === "ACTIVE" ? "report-status-active" : "report-status-default"}`}>
                          {r.status ? r.status.charAt(0) + r.status.slice(1).toLowerCase() : "-"}
                        </span>
                      </td>
                      <td className="td-muted">{objectiveLabel(r.objective) || "-"}</td>
                      <td className="td-muted">{r.dateStart && r.dateStop ? `${r.dateStart} – ${r.dateStop}` : "-"}</td>
                      <td>{r.impressions.toLocaleString()}</td>
                      <td>{r.reach.toLocaleString()}</td>
                      <td>{r.clicks.toLocaleString()}</td>
                      <td>{r.ctr  != null ? `${r.ctr.toFixed(2)}%`  : "-"}</td>
                      <td>${r.spend.toFixed(2)}</td>
                      <td>{r.cpc  != null ? `$${r.cpc.toFixed(2)}`  : "-"}</td>
                      <td>{r.cpm  != null ? `$${r.cpm.toFixed(2)}`  : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="reports-pagination">
                <button
                  className="reports-page-btn"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => typeof p === "string"
                    ? <span key={i} className="reports-page-ellipsis">…</span>
                    : <button key={p} className={`reports-page-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                  )}
                <button
                  className="reports-page-btn"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >›</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
